// DashboardPage.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../../services/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import PageLayout from '../../components/base/PageLayout';
import LeftColumn from './components/LeftColumn';
import RightColumn from './components/RightColumn';
import EditStockModal from './components/EditStockModal';
import AddWatchlistModal from './components/AddWatchlistModal';
import AutomatedTradingDisclaimerModal from './components/AutomatedTradingDisclaimerModal';
import OAuthLoadingOverlay from './components/OAuthLoadingOverlay';
import AccountSelectionModal from '../../components/broker/AccountSelectionModal';
import NotificationsSection from './components/NotificationsSection';
import useStockStore from '../../stores/useStockStore';
import unifiedOAuthService from '../../services/brokers/unifiedOAuthService';
import { useBrokerIntegration } from '../../contexts/BrokerIntegrationContextCompat';
import { useAuthContext } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { normalizeTokenData, normalizeBrokerIntegration } from '../../utils/fieldMapper';
import usePersistentData from '../../hooks/usePersistentData';

const DashboardPage = () => {
  // Debug logging removed to prevent infinite re-renders
  const { 
    stocks, 
    editStock, 
    showAddModal, 
    showRiskDisclosure, 
    showDisclaimerModal,
    showAllSymbolsSelectedPopup,
    setEditStock,
    setShowAddModal,
    setShowRiskDisclosure,
    setShowDisclaimerModal
  } = useStockStore();
  
  const { user } = useAuthContext(); // Get authenticated user
  const { createBrokerLoginNotification } = useNotifications();
  const { 
    storeDashboardState, 
    getDashboardState, 
    useAutoSaveDashboard,
    storeLastVisitedPage,
    getUserPreferences 
  } = usePersistentData();
  
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [currentBroker, setCurrentBroker] = useState('');
  const [showAccountSelection, setShowAccountSelection] = useState(false);
  const [selectedBrokerIntegration, setSelectedBrokerIntegration] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { addBrokerIntegration, refreshIntegrations, brokerIntegrations, hasActiveConnections } = useBrokerIntegration();
  
  const totalValue = stocks.reduce((sum, stock) => sum + (stock.quanlity || 0), 0);

  // Debug: Monitor editStock changes
  useEffect(() => {
    console.log('🔍 Dashboard: editStock changed to:', editStock);
  }, [editStock]);

  // Restore data from persistent storage on component mount
  useEffect(() => {
    const restoreData = () => {
      try {
        // Get stored dashboard state
        const storedDashboardState = getDashboardState();
        console.log('🔍 Restoring dashboard state:', storedDashboardState);
        
        // Get stored user preferences
        const userPreferences = getUserPreferences();
        console.log('🔍 Restoring user preferences:', userPreferences);
        
        // You can restore specific data here if needed
        // For example, restore stocks, filters, etc.
        
        console.log('✅ Dashboard data restored from persistent storage');
      } catch (error) {
        console.error('❌ Error restoring dashboard data:', error);
      }
    };
    
    restoreData();
  }, [getDashboardState, getUserPreferences]);

  // Store current page in persistent storage
  useEffect(() => {
    storeLastVisitedPage('/dashboard');
  }, [storeLastVisitedPage]);

  // Auto-save dashboard state (only when component unmounts)
  useEffect(() => {
    return () => {
      // Save dashboard state when component unmounts
      const dashboardState = {
        stocks,
        totalValue,
        currentBroker,
        showAccountSelection,
        lastUpdated: new Date().toISOString()
      };
      storeDashboardState(dashboardState);
      console.log('💾 Saved dashboard state on unmount');
    };
  }, [stocks, totalValue, currentBroker, showAccountSelection, storeDashboardState]);

  // Broker connection check is now handled by BrokerConnectionGuard
  // No need to check here anymore

  // Add ref to track if OAuth is being processed
  const isProcessingOAuthRef = useRef(false);
  
  // Handle OAuth callback - DISABLED because we use /broker/callback route instead
  useEffect(() => {
    // OAuth callbacks should now be handled by the dedicated /broker/callback route
    // This prevents duplicate saves to the database
    return; // Exit early - don't process OAuth here
    
    // Check if we have OAuth params to process
    const urlParams = new URLSearchParams(location.search);
    const hasOAuthParams = urlParams.has('code');
    
    // Only process OAuth if user is authenticated
    if (!user && hasOAuthParams) {
      console.log('⚠️ User not authenticated, skipping OAuth processing entirely');
      // Clear OAuth params from URL
      navigate('/dashboard', { replace: true });
      return;
    }
    
    if (!user) return;
    
    const handleOAuthCallback = async () => {
      
      const urlParams = new URLSearchParams(location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const scope = urlParams.get('scope');
      
      // Check if we're already processing or have processed this code
      if (!code || isProcessingOAuthRef.current) {
        if (isProcessingOAuthRef.current) {
          console.log('⚠️ OAuth already being processed, skipping duplicate call');
        }
        return;
      }
      
      console.log('🔍 URL params:', { 
        code: code ? `${code.substring(0, 8)}...` : null, 
        scope, 
        state: state ? `${state.substring(0, 8)}...` : null,
        allParams: location.search 
      });
      
      // Mark as processing
      isProcessingOAuthRef.current = true;
      
      console.log('✅ OAuth callback detected with code:', { code: code.substring(0, 8), scope, state: state?.substring(0, 8) });
      
      // Determine broker type from scope first (before setting processing state)
      let detectedBroker = 'Broker';
      if (scope && scope.includes('openid')) {
        detectedBroker = 'TastyTrade';
      } else if (scope && (scope.includes('trade') || scope.includes('market') || scope.includes('stream'))) {
        detectedBroker = 'Tradier';
      } else {
        detectedBroker = 'Schwab';
      }
      
      console.log('🎯 Setting OAuth processing state:', {
        detectedBroker,
        willShowModal: true,
        currentUser: user?.uid
      });
      
      setCurrentBroker(detectedBroker);
      setIsProcessingAuth(true);
      setAuthError(null);
      
      // Force re-render to ensure modal shows
      setTimeout(() => {
        console.log('📊 OAuth processing state check:', {
          isProcessingAuth: true,
          currentBroker: detectedBroker,
          modalShouldBeVisible: true
        });
      }, 100);
        
        try {
          let result;
          
          // Check if this is a TastyTrade callback based on scope
          if (scope && scope.includes('openid')) {
            console.log('🔐 Processing TastyTrade authentication...');
            
            try {
              // Use unified service for consistency
              console.log('📤 Calling exchangeCodeForToken with:', { 
                broker: 'tastytrade', 
                code: code.substring(0, 8) + '...', 
                state: state?.substring(0, 8) + '...',
                timestamp: new Date().toISOString()
              });
              result = await unifiedOAuthService.exchangeCodeForToken('tastytrade', code, state, false);
              
              if (result.success) {
                console.log('TastyTrade account connected successfully:', result.data);
                
                // Normalize and save to broker integration context
                const normalizedTokens = normalizeTokenData(result.data);
                await addBrokerIntegration('tastytrade', {
                  accessToken: normalizedTokens.accessToken,
                  refreshToken: normalizedTokens.refreshToken,
                  expiresAt: normalizedTokens.expiresAt,
                  tokenType: normalizedTokens.tokenType,
                  scope: normalizedTokens.scope,
                  // Add any tastytrade specific data
                  metadata: {
                    lastLogin: new Date().toISOString(),
                    isVerified: true,
                    expirationDate: normalizedTokens.expiresAt
                  }
                });
                
                // Refresh integrations to show the new connection
                await refreshIntegrations();
              }
            } catch (tastytradeError) {
              console.error('❌ TastyTrade OAuth failed:', tastytradeError);
              console.warn('⚠️ TastyTrade OAuth failed (non-critical):', tastytradeError.message);
            }
          }
          // Check if this is a Tradier callback based on scope
          // Tradier returns scope with spaces: "read write trade market stream"
          else if (scope && (scope.includes('trade') || scope.includes('market') || scope.includes('stream'))) {
            console.log('🚀 Processing Tradier authentication with scope:', scope);
            
            try {
              // Use unified service to exchange code for token
              // Use production mode (matching BrokerSelect.jsx)
              console.log('📤 Calling tradier_exchange_token with:', { 
                broker: 'tradier', 
                code: code.substring(0, 8) + '...', 
                state: state?.substring(0, 8) + '...',
                timestamp: new Date().toISOString(),
                url: window.location.href
              });
              result = await unifiedOAuthService.exchangeCodeForToken('tradier', code, state, false);
              
              console.log('📥 Exchange token response:', result);
              
              if (result.success && result.data) {
                console.log('✅ Tradier token exchange successful');
                
                // Normalize token data to ensure consistent field names
                const normalizedTokens = normalizeTokenData(result.data);
                const profileData = result.profile || {};
                
                // Get expiration date from normalized data
                const expiresAt = normalizedTokens.expiresAt;
                
                console.log('📅 Tradier token expiration (normalized):', {
                  original: result.data.expires_at || result.data.expiresAt,
                  normalized: expiresAt,
                  futureDate: new Date(expiresAt)
                });
                
                // Save to broker integration context with normalized field names
                await addBrokerIntegration('tradier', {
                  accessToken: normalizedTokens.accessToken,
                  refreshToken: normalizedTokens.refreshToken,
                  expiresAt: expiresAt,  // Use consistent camelCase
                  tokenType: normalizedTokens.tokenType,
                  scope: normalizedTokens.scope,
                  accountType: profileData.account_type || 'margin',
                  accountId: profileData.account_number || profileData.account_id,
                  accountNumber: profileData.account_number,
                  // User information
                  userName: profileData.name || 'Tradier User',
                  userEmail: profileData.email || 'tradier@user.com', 
                  userUsername: profileData.account_number || 'tradier_user',
                  // Display information
                  brokerDisplayName: 'Tradier',
                  accountName: profileData.account_name || `Tradier Account`,
                  // Add metadata for display
                  metadata: {
                    accountType: profileData.account_type || 'margin',
                    connectedUsername: profileData.account_number,
                    lastLogin: new Date().toISOString(),
                    isVerified: true,
                    expirationDate: expiresAt,  // Use normalized expiration date
                    selectedAccount: profileData.account_number || profileData.account_id,
                    selectedAccountData: {
                      accountNumber: profileData.account_number || null,
                      type: profileData.account_type || 'margin',
                      name: profileData.account_name || null,
                      balance: profileData.balance || null,
                      buyingPower: profileData.buying_power || null
                    },
                    lastAccountRefresh: new Date().toISOString()
                  }
                });
                
                // Force refresh integrations to get latest data from Firestore
                await refreshIntegrations();
                
                // Double-check by refreshing again after a delay
                setTimeout(async () => {
                  await refreshIntegrations();
                  console.log('✅ Double-refreshed integrations to ensure latest data');
                }, 1000);
                
                console.log('✅ Tradier account connected and saved successfully');
                
                // Create broker login notification
                try {
                  const accountInfo = {
                    accountId: profileData.account_number || profileData.account_id,
                    accountName: profileData.name || 'Tradier User'
                  };
                  
                  await createBrokerLoginNotification('tradier', accountInfo);
                  console.log('✅ Broker login notification created');
                } catch (notificationError) {
                  console.warn('⚠️ Failed to create broker notification:', notificationError);
                }
              } else {
                throw new Error(result.error || 'Token exchange returned unsuccessful response');
              }
            } catch (tradierError) {
              console.error('❌ Tradier OAuth failed:', tradierError);
              // Don't show error banner for OAuth failures, just log them
              console.warn('⚠️ Tradier OAuth failed (non-critical):', tradierError.message);
              return; // Exit without setting error
            }
          }
          // Default to Schwab if no specific scope
          else {
            console.log('Processing Schwab authentication...');
            // TODO: Add Schwab token exchange function
            console.warn('⚠️ Schwab authentication not yet implemented');
            return; // Exit without setting error
          }
          
          // Check if any broker authentication was successful
          const isSuccess = result?.data?.success || result?.success;
          if (isSuccess) {
            console.log(`✅ ${detectedBroker} authentication completed successfully`);
            
            // Keep modal showing for a moment to show success
            setTimeout(() => {
              setIsProcessingAuth(false);
              // Clear URL parameters after modal closes
              navigate('/dashboard', { replace: true });
            }, 2000);
          } else {
            console.warn('⚠️ Authentication not successful, but no critical error');
            setIsProcessingAuth(false);
          }
        } catch (error) {
          console.error('❌ OAuth error:', error);
          console.error('Error details:', {
            message: error.message,
            response: error.response,
            data: error.response?.data,
            stack: error.stack
          });
          
          // Stop processing
          setIsProcessingAuth(false);
          
          // Only show critical errors that affect user experience
          if (error.message.includes('critical') || error.message.includes('fatal')) {
            setAuthError(error.message || 'Failed to connect broker account');
          } else {
            console.warn('⚠️ Non-critical OAuth error:', error.message);
          }
        }
    };
    
    handleOAuthCallback();
  }, [location, navigate, user, addBrokerIntegration, refreshIntegrations]);

  // Show loading while OAuth is processing
  const urlParams = new URLSearchParams(location.search);
  const hasOAuthParams = urlParams.has('code');

  // Loading state is now handled by BrokerConnectionGuard

  return (
    <>
      {/* AddWatchlistModal */}
      <AddWatchlistModal />
      
      {/* OAuth Loading Overlay - Always show when processing OAuth */}
      {isProcessingAuth && (
        <OAuthLoadingOverlay broker={currentBroker || 'Broker'} />
      )}

      {/* EditStockModal - Moved to top level for proper rendering */}
      {/* Debug logging removed to prevent infinite re-renders */}
      {editStock && (
        <EditStockModal
          stock={editStock}
          onClose={() => setEditStock(null)}
        />
      )}



      <PageLayout>
        
        {authError && user && (
          <div className="fixed top-4 right-4 bg-red-900/95 border border-red-500 text-red-100 px-6 py-4 rounded-lg shadow-2xl z-50 max-w-md backdrop-blur-sm">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center mb-2">
                  <svg className="w-5 h-5 mr-2 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold text-sm">Connection Failed</span>
                </div>
                <p className="text-sm text-red-200">{authError}</p>
                <p className="text-xs text-red-300 mt-2">Please try connecting again or contact support if the issue persists.</p>
              </div>
              <button 
                onClick={() => {
                  setAuthError(null);
                  navigate('/dashboard', { replace: true });
                }}
                className="ml-4 text-red-300 hover:text-red-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}
        
        <LeftColumn />

        <RightColumn totalPortfolioValue={totalValue} />
        
        {/* Token Refresh Status Widget - Commented out due to missing import */}
        {/* <div className="mt-6">
          <TokenRefreshStatus />
        </div> */}

        <div className="mt-6">
          {stocks.map((stock) => (
            <div key={stock.symbol} className="bg-gray-800 p-4 mb-4 rounded-lg shadow-lg">
              <div className="flex justify-between items-center">
                <div className="text-white text-lg font-semibold">{stock.symbol}</div>
                <div className="text-white text-sm">
                  ${stock.value.toLocaleString()} — {stock.spread} Spread Hedge {stock.hedge ? 'On' : 'Off'}
                </div>
              </div>
              <div className={`text-${stock.status === 'Filled' ? 'green' : stock.status === 'Paused' ? 'orange' : 'red'}-500`}>
                {stock.status}
              </div>
              <button
                className="mt-2 px-4 py-1 bg-purple-600 text-white rounded-md"
                onClick={() => { console.log('🖊 setEditStock from top list', stock); setEditStock(stock); }}
              >
                Edit
              </button>
            </div>
          ))}
        </div>

        {showRiskDisclosure && (
          <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center px-4">
            <div className="bg-[#0F172A] text-white p-6 rounded-xl w-full max-w-4xl shadow-lg relative z-10">
              <h2 className="text-3xl font-bold mb-4 text-center">Risk Disclosure & Waiver of Liability</h2>
              <div className="overflow-y-auto max-h-96 text-sm">
                <p><strong>WARNING:</strong> ...</p>
                {/* รายละเอียด risk disclosure ตามที่คุณมีอยู่ */}
              </div>
              <div className="flex justify-between mt-6 space-x-2">
                <button
                  onClick={() => setShowRiskDisclosure(false)}
                  className="px-6 py-3 bg-gray-700 rounded hover:bg-gray-600 w-32"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowRiskDisclosure(false);
                    setShowDisclaimerModal(true);
                  }}
                  className="px-6 py-3 bg-purple-600 rounded hover:bg-purple-500 w-32"
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Account Selection Modal */}
        {showAccountSelection && selectedBrokerIntegration && (
          <AccountSelectionModal
            isOpen={showAccountSelection}
            onClose={() => {
              setShowAccountSelection(false);
              setSelectedBrokerIntegration(null);
            }}
            broker={selectedBrokerIntegration.broker}
            onAccountSelected={(selectedAccount) => {
              console.log('✅ Account selected:', selectedAccount);
              setShowAccountSelection(false);
              setSelectedBrokerIntegration(null);
              // Refresh integrations to show updated account selection
              refreshIntegrations();
            }}
          />
        )}


        
        {/* Debug: Show editStock state */}
        {process.env.NODE_ENV === 'development' && (
          <div className="fixed bottom-4 right-4 bg-black text-white p-2 rounded text-xs z-[9999]">
            editStock: {editStock ? editStock.symbol : 'null'}
            <br />
            editStock type: {typeof editStock}
            <br />
            editStock is null: {editStock === null ? 'true' : 'false'}
            <br />
            editStock is undefined: {editStock === undefined ? 'true' : 'false'}
          </div>
        )}

        {showDisclaimerModal && (
          <AutomatedTradingDisclaimerModal
            onClose={() => setShowDisclaimerModal(false)}
            onAccept={() => {
              setShowDisclaimerModal(false);
              // Handle acceptance logic
            }}
          />
        )}


      </PageLayout>
    </>
  );
};

export default DashboardPage;